import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTentativeETALocation } from '@rsos/reduxDataStore/src/features/airAmbulance';
import { useGetTransportTentativeEtaQuery } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { TentativeETA } from '../../types';
import { useAirAmbulanceEtlStatus } from './useAirAmbulanceEtlStatus';

function formatETADisplayText(timestamp: string, label: string): string {
  const date = new Date(timestamp);
  const now = new Date();

  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const diffMinutes = Math.round((date.getTime() - now.getTime()) / 60000);

  return `${label}: ${time} (${diffMinutes} minutes)`;
}

/**
 * Optional settings for `useAirAmbulanceEtlEta`.
 */
interface UseAirAmbulanceEtlEtaOptions {
  enabled?: boolean;
}

/**
 * Return the latest ETA information that is available.
 *
 * If we already have a request selected and the ETA is available from the
 * status information, we use the ETA provided by GMR in the status update.
 * Otherwise we fetch the tentative ETA from GMR using the etl-integrations
 * service. The "tentative ETA" is an estimate GMR provides when a transport
 * hasn't been dispatched yet.
 *
 * Set `enabled` to false to skip the tentative ETA request so that status-derived ETA
 * from useAirAmbulanceEtlStatus still renders when present. Use false on views
 * that are not the Air Ambulance detail form, like the transport rows on the resource list.
 */
export function useAirAmbulanceEtlEta(
  options: UseAirAmbulanceEtlEtaOptions = {},
): TentativeETA | null {
  const { enabled = true } = options;
  const tentativeETALocation = useSelector(selectTentativeETALocation);
  const { latestProvider, latestEta, latestStatus } =
    useAirAmbulanceEtlStatus();
  // Tentative ETA only applies before any GMR status rows exist.
  const hasStatusUpdates = latestStatus != null;
  const shouldFetchEta =
    enabled && !latestEta && !hasStatusUpdates && Boolean(tentativeETALocation);
  const queryLocation = useMemo(() => {
    if (!shouldFetchEta || !tentativeETALocation) {
      return skipToken;
    }

    const decimalPlaces = 5;

    return {
      latitude: Number(tentativeETALocation.latitude.toFixed(decimalPlaces)),
      longitude: Number(tentativeETALocation.longitude.toFixed(decimalPlaces)),
    };
  }, [shouldFetchEta, tentativeETALocation]);

  const { data } = useGetTransportTentativeEtaQuery(queryLocation);

  if (latestEta) {
    // Case where we get the ETA from status updates of an already dispatched
    // transport.  The label reflects whether the transport has already arrived.
    const label = latestEta.isArrived ? 'Arrived at' : 'ETA';
    return {
      display: formatETADisplayText(latestEta.utcTime, label),
      providers: latestProvider?.name || null,
    };
  }

  if (data && !hasStatusUpdates) {
    // Case where we don't have a status update from a dispatched transport,
    // and we had to query GMR to get a tentative ETA (i.e. estimate).
    // Label should always reflect that it is tentative.
    return {
      display: formatETADisplayText(data.eta, 'Tentative ETA'),
      providers: data.providers,
    };
  }

  return null;
}
